import { useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Default fallback values
const DEFAULT_SETTINGS = {
  industries: [
    { id: 'energy', title: 'Энергетика' },
    { id: 'industrial', title: 'Промышленность' },
    { id: 'infrastructure', title: 'Инфраструктура' },
    { id: 'oil_gas', title: 'Нефть и газ' },
    { id: 'transport', title: 'Транспорт' }
  ],
  project_stages: [
    { id: 'research', title: 'Исследование' },
    { id: 'pre_tender', title: 'Предтендер' },
    { id: 'tender_preparation', title: 'Подготовка тендера' },
    { id: 'commercial_proposal', title: 'Коммерческое предложение' },
    { id: 'negotiations', title: 'Переговоры' },
    { id: 'won', title: 'Выиграно' },
    { id: 'lost', title: 'Проиграно' }
  ],
  project_flags: [
    { id: 'HOT', title: '🔥 Горячий' },
    { id: 'COLD', title: '❄️ Холодный' },
    { id: 'PRIORITY', title: '⭐ Приоритетный' }
  ]
};

export const useSystemSettings = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/system-settings`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      const apiSettings = response.data;
      
      // Convert API format to component format
      const formattedSettings = {
        industries: apiSettings.industries?.map((name, index) => {
          // Map Russian industry names to backend enum values
          const industryMapping = {
            'Энергетика': 'energy',
            'Промышленность': 'mining',
            'Инфраструктура': 'infrastructure', 
            'Нефть и газ': 'oil_gas',
            'Транспорт': 'commercial',
            'СЭС': 'solar'
          };
          
          return {
            id: industryMapping[name] || name.toLowerCase().replace(/\s+/g, '_'),
            title: name
          };
        }) || DEFAULT_SETTINGS.industries,
        
        project_stages: apiSettings.project_stages?.map(stageId => ({
          id: stageId,
          title: apiSettings.stage_labels?.[stageId] || stageId
        })) || DEFAULT_SETTINGS.project_stages,
        
        project_flags: apiSettings.project_flags?.map(flagId => ({
          id: flagId,
          title: apiSettings.flag_labels?.[flagId] || flagId
        })) || DEFAULT_SETTINGS.project_flags,
        
        // Add task templates from API response
        task_templates: apiSettings.task_templates || []
      };
      
      setSettings(formattedSettings);
    } catch (err) {
      console.warn('Failed to load system settings, using defaults:', err);
      setError(err);
      setSettings(DEFAULT_SETTINGS);
    } finally {
      setLoading(false);
    }
  };

  return {
    settings,
    loading,
    error,
    reload: loadSettings,
    // Helper getters
    industries: settings?.industries || DEFAULT_SETTINGS.industries,
    stages: settings?.project_stages || DEFAULT_SETTINGS.project_stages,
    flags: settings?.project_flags || DEFAULT_SETTINGS.project_flags,
    taskTemplates: settings?.task_templates || [],
    
    // Helper functions
    getIndustryLabel: (industryId) => {
      const industry = settings?.industries?.find(i => i.id === industryId);
      return industry?.title || industryId;
    },
    
    getStageLabel: (stageId) => {
      const stage = settings?.project_stages?.find(s => s.id === stageId);
      return stage?.title || stageId;
    },
    
    getFlagLabel: (flagId) => {
      const flag = settings?.project_flags?.find(f => f.id === flagId);
      return flag?.title || flagId;
    }
  };
};